<script setup>
import { storeToRefs } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import { useTransactionStore } from '@/stores/transaction'
import { useBrokerSelectionStore } from '@/stores/brokerSelectionStore'
import { useFeature } from '@/composables/useFeature'
import { useHelpers } from '@/composables/useHelpers'
import TransactionsEhrInfoComponent from './sidebar/TransactionsEhrInfoComponent.vue'
import TransactionsInfoComponent from './sidebar/TransactionsInfoComponent.vue'
import TransactionsMaatukkInfoComponent from './sidebar/TransactionsMaatukkInfoComponent.vue'

const transactionStore = useTransactionStore()
const { selectedBuilding, selectedTransaction } = storeToRefs(transactionStore)

const brokerSelectionStore = useBrokerSelectionStore()

const props = defineProps({
  objectId: Number,
  marginTop: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['toggleMark', 'toggleBrokerMark'])

const tabIndex = ref(0)
const isScrolled = shallowRef(false)

const handleScroll = event => {
  isScrolled.value = event.target.scrollTop > 0
}

const selectedBuildings = computed(() => {
  const selectedId = selectedTransaction.value?.id
  return selectedId && transactionStore.buildings[selectedId]
    ? transactionStore.buildings[selectedId]
    : []
})

// Closing the drawer clears the store immediately (that's what stops the map
// halo on time), which would unmount the whole panel tree on the same frames
// as the 300ms slide-out. Freeze the content in a snapshot instead and drop
// it in @after-hide, so the unmount happens after the animation.
const closingSnapshot = shallowRef(null)

const displayedTransaction = computed(
  () => selectedTransaction.value ?? closingSnapshot.value?.transaction ?? null,
)

const displayedBuildings = computed(() =>
  selectedTransaction.value
    ? selectedBuildings.value
    : (closingSnapshot.value?.buildings ?? []),
)

const onAfterHide = () => {
  closingSnapshot.value = null
}

const { isKasiLikeTransaction } = useHelpers()

const showEhitisTab = computed(
  () => displayedBuildings.value.length > 0 && !!selectedBuilding.value,
)

// RED-267: unbuilt KASI/HÕIG land parcel. COEXISTS with the Ehitis tab —
// thousands of unbuilt-KASI transactions carry permit-stage (Kavandatav)
// buildings, so never gate this on the buildings list being empty.
const showMaatukkTab = computed(
  () =>
    !!displayedTransaction.value &&
    isKasiLikeTransaction(displayedTransaction.value) &&
    displayedTransaction.value.hoonestatud === 0,
)

// Keep each tab at ~1/4 of the header width regardless of how many render.
const tabListWidthClass = computed(() => {
  const count = 1 + (showEhitisTab.value ? 1 : 0) + (showMaatukkTab.value ? 1 : 0)
  return { 1: 'w-1/4', 2: 'w-1/2', 3: 'w-3/4' }[count]
})

const markedTransactions = computed(
  () => transactionStore.getMarkedTransactions,
)

const transactionIsMarked = computed(() => {
  return (
    displayedTransaction.value &&
    markedTransactions.value.includes(displayedTransaction.value.id)
  )
})

// RED-180 (2026-05-27): broker selection is fully separate from the
// Hindamine `markedTransactions` set. The broker button only appears on
// `/transactions` standalone (no objectId), behind the `broker-export`
// feature flag (RED-242), and writes to its own Pinia store. The original
// Hindamine mark button above is unchanged.
const brokerExport = useFeature('broker-export')
const brokerMarkEnabled = computed(() => !props.objectId && brokerExport.value)

const transactionIsBrokerSelected = computed(() => {
  return (
    displayedTransaction.value &&
    brokerSelectionStore.has(displayedTransaction.value.id)
  )
})

// Disable add when at the 5-transaction cap. Unmarking is always allowed
// (so a user at the limit can still drop a row to make space).
const brokerLimitReached = computed(
  () => brokerSelectionStore.isAtLimit && !transactionIsBrokerSelected.value,
)

const handleClose = () => {
  closingSnapshot.value = {
    transaction: selectedTransaction.value,
    buildings: selectedBuildings.value,
  }
  transactionStore.handleMapFeatureSelection([])
}

const toggleMark = async () => {
  if (selectedTransaction.value) {
    await transactionStore.toggleMarked(selectedTransaction.value)
    emit('toggleMark', selectedTransaction.value)
  }
}

const toggleBrokerMark = () => {
  if (selectedTransaction.value) {
    brokerSelectionStore.toggle(selectedTransaction.value.id)
    emit('toggleBrokerMark', selectedTransaction.value)
  }
}

watch(
  () => transactionStore.selectedTransactionIds,
  (newIds, oldIds) => {
    // Empty ids = closing: keep the snapshot-frozen tab through the slide-out.
    if (!newIds?.length) {
      return
    }
    if (newIds.length !== oldIds?.length || newIds[0] !== oldIds?.[0]) {
      tabIndex.value = 0
    }
  },
  { deep: true },
)
</script>

<template>
  <Drawer
    :visible="!!transactionStore.selectedTransactionIds?.length"
    @update:visible="handleClose"
    @after-hide="onAfterHide"
    position="right"
    :baseZIndex="50"
    id="transactions-sidebar"
    data-testid="txn-sidebar"
    class="w-136"
    :class="{ 'pb-4': marginTop}"
    :pt:mask:class="'transactions-sidebar-mask'"
    :pt:header:class="{ 'is-scrolled z-10': isScrolled, 'transition-shadow duration-200': !isScrolled }"
    :dt="{
        content: {
            padding: '0rem'
        }
    }"
  >
    <template #header>
      <Button
        v-if="objectId && displayedTransaction"
        severity="danger"
        class="absolute top-28 -left-6 z-10"
        size="small"
        :icon="transactionIsMarked ? 'pi pi-minus' : 'pi pi-plus'"
        @click="toggleMark"
        v-tooltip.left="{
          value: $t(
            'messages.transactions.' +
              (transactionIsMarked ? 'unmark_for_comparison' : 'mark_for_comparison')
          ),
          pt: { text: 'break-normal!' },
        }"
      />
      <!-- RED-180 broker mark button — only on /transactions standalone
           (no objectId). Distinct severity (warn / orange) from the
           Hindamine red mark above so the two flows are never confused.
           Capped at 5 selections (Siim 2026-05-27); past the cap the
           button is disabled unless the user is unmarking an already-
           selected transaction. -->
      <Button
        v-if="brokerMarkEnabled && displayedTransaction"
        severity="warn"
        class="absolute top-28 -left-6 z-10"
        size="small"
        :icon="transactionIsBrokerSelected ? 'pi pi-times' : 'pi pi-plus'"
        :disabled="brokerLimitReached"
        data-testid="txn-sidebar-broker-mark"
        @click="toggleBrokerMark"
        v-tooltip.left="{
          value: brokerLimitReached
            ? $t('messages.markedtransactions.broker-limit-reached')
            : $t(
                'messages.markedtransactions.' +
                  (transactionIsBrokerSelected ? 'broker-remove-row' : 'broker-add-to-table')
              ),
          pt: { text: 'break-normal!' },
        }"
      />
      <div class="flex justify-between items-center w-full">
        <Tabs v-if="displayedTransaction" v-model:value="tabIndex" class="flex-1 items-center">
          <TabList class="flex" :class="tabListWidthClass">
            <Tab :value="0" class="flex-1" data-testid="txn-sidebar-tab-tehing">{{ $t('messages.transactions.fields.tehing') }}</Tab>
            <Tab v-if="showEhitisTab" :value="1" class="flex-1" data-testid="txn-sidebar-tab-ehitis">Ehitis</Tab>
            <Tab v-if="showMaatukkTab" :value="2" class="flex-1" data-testid="txn-sidebar-tab-maatukk">{{ $t('messages.transactions.maatukk.tab') }}</Tab>
          </TabList>
        </Tabs>
        <h3 v-else class="text-lg font-bold">
          {{ $t('messages.transactions.title') }}
        </h3>
      </div>
    </template>

    <Tabs v-model:value="tabIndex" class="flex flex-col h-full">
      <TabPanels
        @scroll="handleScroll"
        class="grow overflow-y-auto custom-scrollbar"
      >
        <TabPanel v-if="displayedTransaction" :value="0">
          <TransactionsInfoComponent :transaction="displayedTransaction" />
        </TabPanel>
        <TabPanel v-if="displayedTransaction" :value="1">
          <div class="flex w-full mt-4 px-4">
            <Select
              v-if="displayedBuildings.length > 0"
              v-model="selectedBuilding"
              :options="displayedBuildings"
              optionLabel="ehitise_nimetus"
              :placeholder="$t('messages.transactions.select_building')"
              fluid
            />
          </div>

          <TransactionsEhrInfoComponent
            v-if="displayedBuildings.length > 0 && selectedBuilding"
            :key="selectedBuilding"
            :title="
              selectedBuilding?.ehitise_nimetus
                ? selectedBuilding.ehitise_nimetus
                : `${$t('messages.transactions.hoone')}${
                    displayedBuildings.length < 2 ? '' : ' ' + (i + 1)
                  }`
            "
            :building="selectedBuilding"
          />
          <div v-else class="p-4 text-center text-gray-500">
            {{ $t('messages.transactions.no_ehr_data') }}
          </div>
        </TabPanel>
        <TabPanel v-if="displayedTransaction && showMaatukkTab" :value="2">
          <TransactionsMaatukkInfoComponent :transaction="displayedTransaction" />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </Drawer>
</template>

<style>
  @reference '../../../css/tailwind.css';

  /* Perf: PrimeVue keeps the modal mask in the DOM until the 300ms leave
     animation ends — it swallows every pointermove (dead hover/tooltip on
     the map for the whole slide-out) and its background keyframes force a
     full-viewport repaint per frame over the WebGL canvas. Make the LEAVING
     mask inert and paint-free; the open-state dim is unchanged. */
  .transactions-sidebar-mask.p-overlay-mask-leave-active {
    pointer-events: none;
    animation: none !important;
    background: transparent !important;
  }

  #transactions-sidebar .p-tabpanels {
    padding: 0 !important;
  }

  #transactions-sidebar .is-scrolled {
    @apply shadow-md shadow-gray-400/30;
  }


  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgb(203 213 225) transparent;

    &::-webkit-scrollbar {
      width: 12px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      @apply bg-gray-300 dark:bg-gray-600;
      border-radius: 10px;
      border: 2px solid transparent;
      background-clip: content-box;

      &:hover {
        @apply bg-gray-400 dark:bg-gray-500;
      }

      &:active {
        @apply bg-gray-500 dark:bg-gray-400;
      }
    }
  }

</style>
